
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleMinus,
  faEllipsis,
  faPlus,
  faPenToSquare,
} from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getAllTokens, deleteToken } from "../../api/Tokens";
import { useAuth, Roles } from "../../providers/AuthContext";
import TdLink from "../../components/TdLink";
import CustomDropdown from "../../components/CustomDropdown";
import DataTable from "../../components/DataTable";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { getOrganizationDisplayName } from "../../helpers/CustomerDisplay";
import { useTranslation } from "react-i18next";
import { getDecryptedTokenDisplay, getTokenValidLabel } from "../../helpers/TokenValidity";

const TokensPage = () => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [search, setSearch] = useState("");
  const [tokensLoaded, setTokensLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => { },
  });

  useEffect(() => {
    handleGetTokens();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const handleGetTokens = async () => {
    try {
      const accessToken = await getAccessToken();
      const newTokens = await getAllTokens(accessToken);
      setTokens(newTokens);
      setTokensLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };


  const handleDeleteToken = async (idTagPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.token") }));
    try {
      const accessToken = await getAccessToken();
      await deleteToken(idTagPublicId, accessToken);
      const newTokens = await getAllTokens(accessToken);
      setTokens(newTokens);
      toast.success(t("toast.deleted", { entity: t("entity.token") }), { id: toastId });
    } catch (error) {
      toast.error(t("toast.failedDelete", { entity: t("entity.token") }), { id: toastId });
      console.error(error);
    }
  };

  const columns = [
    {
      key: "name",
      header: t("common.name"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[100px]",
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => token.name || token.visualNumber || "",
      render: (token) => {
        return (
          <TdLink className="min-w-[100px]" to={`/token/${token.idTagPublicId}`}>
            {token.name || token.visualNumber}
          </TdLink>
        );
      },
    },
    {
      key: "issuer",
      header: t("table.issuer"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[100px]",
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => token.issuer || "Gito",
      render: (token) => {
        return (
          <TdLink className="min-w-[100px]" to={`/token/${token.idTagPublicId}`}>
            {token.issuer || "Gito"}
          </TdLink>
        );
      },
    },
    {
      key: "customerName",
      header: t("entity.customer"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[140px]",
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => token.customerName || "",
      render: (token) => {
        return (
          <TdLink className="min-w-[140px]" to={`/token/${token.idTagPublicId}`}>
            {token.customerName || ""}
          </TdLink>
        );
      },
    },
    {
      key: "organizationName",
      header: t("entity.organization"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[140px]",
      hidden: true,
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => getOrganizationDisplayName(token),
      render: (token) => {
        return (
          <TdLink className="min-w-[140px]" to={`/token/${token.idTagPublicId}`}>
            {getOrganizationDisplayName(token)}
          </TdLink>
        );
      },
    },
    {
      key: "tagToken",
      header: t("entity.token"),
      sortable: true,
      rawCell: true,
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => token.tagToken || "",
      render: (token) => {
        return (
          <TdLink className="break-all" to={`/token/${token.idTagPublicId}`}>
            {token.tagToken || ""}
          </TdLink>
        );
      },
    },
    {
      key: "decryptedTagToken",
      header: t("token.decrypted"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[140px]",
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => getDecryptedTokenDisplay(token, t),
      render: (token) => {
        return (
          <TdLink className="break-all min-w-[140px]" to={`/token/${token.idTagPublicId}`}>
            {getDecryptedTokenDisplay(token, t)}
          </TdLink>
        );
      },
    },
    {
      key: "tokenType",
      header: t("common.type"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[90px]",
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => token.tokenType || "",
      render: (token) => {
        return (
          <TdLink className="min-w-[90px]" to={`/token/${token.idTagPublicId}`}>
            {token.tokenType || ""}
          </TdLink>
        );
      },
    },
    {
      key: "valid",
      header: t("common.valid"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[90px]",
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => getTokenValidLabel(token, t),
      render: (token) => {
        return (
          <TdLink className="min-w-[90px]" to={`/token/${token.idTagPublicId}`}>
            {getTokenValidLabel(token, t)}
          </TdLink>
        );
      },
    },
    {
      key: "isFromOcpi",
      header: t("table.externalToken"),
      sortable: true,
      rawCell: true,
      headerClassName: "min-w-[110px]",
      disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
      getSortValue: (token) => (token.isFromOcpi ? "yes" : "no"),
      render: (token) => {
        return (
          <TdLink className="min-w-[110px]" to={`/token/${token.idTagPublicId}`}>
            {token.isFromOcpi ? t("common.yes") : t("common.no")}
          </TdLink>
        );
      },
    },
    {
      key: "actions",
      header: "",
      sortable: false,
      rawCell: true,
      searchable: false,
      headerClassName: "min-w-[64px]",
      render: (token) => {
        const isOcpiCreated = token.lastUpdatedBy === "OCPI: Created via PUT /token";

        return (
          <td>
            <div className="flex justify-end">
              {token.isFromOcpi === false && (
                <CustomDropdown
                  isRight={true}
                  ContentComponent={() => (
                    <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                      {!isOcpiCreated ? (
                        <NavLink
                          className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                          to={`/token/${token.idTagPublicId}`}
                        >
                          <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                        </NavLink>
                      ) : (
                        ""
                      )}

                      <button
                        className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                        onClick={() => {
                          setConfirmState({
                            isOpen: true,
                            title: t("confirm.deleteEntity", {
                              entity: t("entity.token"),
                              name: token.name || token.visualNumber,
                            }),
                            confirm: () => {
                              handleDeleteToken(token.idTagPublicId);
                            },
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                      </button>
                    </div>
                  )}
                >
                  <div className="w-8 h-6 flex justify-end bg-red items-center cursor-tokener">
                    <FontAwesomeIcon icon={faEllipsis} />
                  </div>
                </CustomDropdown>
              )}
            </div>
          </td>
        );
      },
    },
  ];

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            
          <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.tokens")} loading={!tokensLoaded} />
            </div>

            <div className={`${!tokensLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("pages.chargingTokens")}</h2>
                <NavLink
                  className="btn btn-primary"
                  to={`/token/create`}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createToken")}
                </NavLink>
              </div>

              <div className="rounded-md shadow-md w-full p-4">
                <DataTable
                    rows={tokens}
                    columns={columns}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder={t("common.search")}
                    rowKey={(token, index) => token.idTagPublicId || index}
                    emptyColSpan={hasRole(Roles.Admin) ? 10 : 9}
                  />
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default TokensPage;

